/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useNavigationStore } from "@/stores/useNavigationStore";
import { useRedirectStore } from "@/stores/useRedirectStore";
import { useCompaniesTableStore } from "@/stores/useTableStore";
import Typography from "@mui/material/Typography";

import { ChevronRightIcon } from "@dotnet/telematics-design-system";

import useChangeSelectedCompany from "../../../../components/Navbar/components/CompanySelector/hooks/useChangeSelectedCompany";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { useAuth } from "../../../../context/Auth/AuthProvider";
import { ROUTES } from "../../../../shared";
import { CONNECTIVITY_TABS } from "../../types";
import useDeviceStatusFunctions from "../DeviceStatus/hooks/useDeviceStatusFunctions";
import {
  CompaniesListFunctionDataProps,
  StatusCompanyDto,
  StatusSummaryType
} from "../DeviceStatus/types";

const useRenderCompaniesList = () => {
  const { t } = useTranslation();

  const { user } = useAuth();
  const {
    handleStatusInfoListItemClick: handleDeviceStatusInfoListItemClick,
    handleResourceDownloadClick
  } = useDeviceStatusFunctions();

  const { setTakeToCompanies } = useRedirectStore();

  const { changeSelectedCompany } = useChangeSelectedCompany();

  const navigate = useNavigate();

  const { setParams, resetExceptPaginationAndColumns } =
    useCompaniesTableStore();

  const handleCCstatusInfoListItemClick = async (
    itemData: StatusCompanyDto
  ) => {
    resetExceptPaginationAndColumns();

    if (itemData.id === user.selectedCompany.id) {
      navigate(ROUTES.Companies);
    } else {
      try {
        useNavigationStore.getState().setTargetRoute(ROUTES.Companies);
        await changeSelectedCompany(itemData.id, false, ROUTES.Companies);
      } finally {
        useNavigationStore.getState().setTargetRoute(null);
      }
    }

    setParams({
      desktopDetailsSection: true,
      selectedTableItem: itemData.id
    });

    setTakeToCompanies(itemData.id);
  };

  const handleStatusInfoClick = (
    itemData: StatusCompanyDto,
    infoData: StatusSummaryType,
    activeTab?: CONNECTIVITY_TABS
  ) => {
    if (activeTab && activeTab === CONNECTIVITY_TABS.Devices) {
      handleDeviceStatusInfoListItemClick(itemData, infoData);
    } else {
      handleCCstatusInfoListItemClick(itemData);
    }
  };

  const handleCompanyClick = (
    company: StatusCompanyDto,
    infoData: StatusSummaryType,
    activeTab?: CONNECTIVITY_TABS,
    resourceDownloadLocation?: ROUTES
  ) => {
    if (resourceDownloadLocation) {
      return handleResourceDownloadClick(
        company,
        infoData,
        resourceDownloadLocation
      );
    }

    return handleStatusInfoClick(company, infoData, activeTab);
  };

  const renderCompaniesList = ({
    infoData,
    statusInfoListItem,
    hasCardWrapper,
    arrowStyle,
    hoverableListItem,
    activeTab,
    resourceDownloadLocation
  }: CompaniesListFunctionDataProps) =>
    infoData.companies.map((company: StatusCompanyDto) => (
      <div
        key={company.name}
        css={css([statusInfoListItem])}
        onClick={() =>
          handleCompanyClick(
            company,
            infoData,
            activeTab,
            resourceDownloadLocation
          )
        }
      >
        <Typography variant="body3Medium" sx={{ width: "40px" }}>
          {company.count}
        </Typography>

        <Tooltip
          data-testid={`status-company-list-item-tooltip-${company.name}`}
          title={t(`Dashboard##tooltip##take me there`)}
          placement="top"
          size="medium"
          hasArrow
        >
          {hasCardWrapper ? (
            <Typography variant="body3Medium">{company.name}</Typography>
          ) : (
            <span css={css(hoverableListItem)}>
              <Typography variant="body3Medium">{company.name}</Typography>
              <ChevronRightIcon css={css(arrowStyle)} />
            </span>
          )}
        </Tooltip>
      </div>
    ));

  return {
    renderCompaniesList
  };
};

export default useRenderCompaniesList;
